import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UserSessionSelector, updateUser } from 'store/UserSession';
import axiosPrivate from 'config/axiosPrivate';
import { HELP_VIDEO_LIBRARY } from '../UseCasePanel/helpData';
import './NewVideosLoginModal.css';

const HIDE_TUTORIALS_POPIN_URL = '/user/admin-client/tutorials-popin/hide';

const VIDEO_DESCRIPTIONS = {
  onboarding:
    'Un tour d’horizon rapide de la plateforme et de ses principales fonctionnalités.',
  'agent-ia': 'Découvrez comment utiliser l’Agent IA de Typix Néo.',
  'generateur-brief':
    'Apprenez à générer un brief de rédaction en quelques clics.',
  'decouverte-mots-cles': 'Trouvez les mots-clés à cibler pour vos contenus.',
  'redaction-ia':
    'Générez des contenus optimisés grâce à la rédaction IA de Typix Néo.',
  'score-contenu':
    'Évaluez la qualité SEO et éditoriale de vos contenus avec le scoring.',
  'creation-projet':
    'Créez et organisez vos projets de contenu étape par étape.',
  'redaction-en-lot':
    'Produisez plusieurs contenus en une seule fois avec la rédaction en lot.',
};

const VIDEO_PLAYLIST = HELP_VIDEO_LIBRARY.map((video) => ({
  ...video,
  duration: video.id === 'onboarding' ? '2:00' : '',
  descriptionKey: VIDEO_DESCRIPTIONS[video.id] || '',
}));

const WATCHED_KEY = 'typix-new-videos-watched-v2';

function readJsonArray(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeJsonArray(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function CheckIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M8 5.5v13l11-6.5z" />
    </svg>
  );
}

export default function NewVideosLoginModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector(UserSessionSelector);
  const videoRef = useRef(null);

  const [dismissed, setDismissed] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHiding, setIsHiding] = useState(false);
  const [watchedIds, setWatchedIds] = useState(() => readJsonArray(WATCHED_KEY));
  const [durations, setDurations] = useState({});

   useEffect(() => {
    if (user?.tutorialsPopinSeenVersion === true) {
      setSessionOpen(true);
    }
  }, [user?.tutorialsPopinSeenVersion]);

  const open = sessionOpen && !dismissed;

  const currentVideo = VIDEO_PLAYLIST[activeIndex] || VIDEO_PLAYLIST[0];
  const isLastVideo = activeIndex === VIDEO_PLAYLIST.length - 1;
  const watchedCount = useMemo(
    () => VIDEO_PLAYLIST.filter((video) => watchedIds.includes(video.id)).length,
    [watchedIds]
  );
  const progressPct = VIDEO_PLAYLIST.length
    ? Math.round((watchedCount / VIDEO_PLAYLIST.length) * 100)
    : 0;

  const markWatched = useCallback((videoId) => {
    if (!videoId) return;
    setWatchedIds((prev) => {
      if (prev.includes(videoId)) return prev;
      const next = [...prev, videoId];
      writeJsonArray(WATCHED_KEY, next);
      return next;
    });
  }, []);

  const persistClose = useCallback(() => {
    setDismissed(true);
    setSessionOpen(false);
    setIsPlaying(false);
    setIsHiding(false);
  }, []);

  const selectVideo = (index) => {
    setActiveIndex(index);
    setIsPlaying(false);
  };

  const playNext = () => {
    if (!VIDEO_PLAYLIST.length || isLastVideo) return;
    setIsPlaying(false);
    setActiveIndex((prev) => prev + 1);
  };

  const hideTutorialsPopin = useCallback(async () => {
    if (isHiding) return;
    setIsHiding(true);
    try {
      await axiosPrivate.patch(HIDE_TUTORIALS_POPIN_URL);
      if (user) {
        dispatch(
          updateUser({
            ...user,
            tutorialsPopinSeenVersion: false,
          })
        );
      }
    } catch {
      // keep local preference even if API fails
    } finally {
      setIsHiding(false);
    }
  }, [isHiding, user, dispatch]);

  const handleDontShowAgainChange = (checked) => {
    setDontShowAgain(checked);
    if (checked) {
      hideTutorialsPopin();
    }
  };

  const handlePrimaryAction = () => {
    if (isLastVideo) {
      persistClose();
      return;
    }
    playNext();
  };

  const handlePlayClick = () => {
    const video = videoRef.current;
    if (!video) return;
    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
    setIsPlaying(true);
  };

  const setVideoDuration = useCallback((videoId, duration) => {
    if (!videoId || !Number.isFinite(duration) || duration <= 0) return;
    setDurations((prev) =>
      prev[videoId] === duration ? prev : { ...prev, [videoId]: duration }
    );
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') persistClose();
    };

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, persistClose]);

  useEffect(() => {
    if (!open || !videoRef.current || !currentVideo?.src) return;
    const video = videoRef.current;
    video.load();
    setIsPlaying(false);
  }, [open, activeIndex, currentVideo?.src]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const elements = [];

    VIDEO_PLAYLIST.forEach((item) => {
      const el = document.createElement('video');
      el.preload = 'metadata';
      el.muted = true;
      el.playsInline = true;
      el.src = item.src;
      elements.push(el);

      const applyDuration = () => {
        if (cancelled) return;
        setVideoDuration(item.id, el.duration);
      };

      el.addEventListener('loadedmetadata', applyDuration);
      el.addEventListener('durationchange', applyDuration);
      el.load();
      if (el.readyState >= 1) applyDuration();
    });

    return () => {
      cancelled = true;
      elements.forEach((el) => {
        el.removeAttribute('src');
        try {
          el.load();
        } catch {
          // ignore
        }
      });
    };
  }, [open, setVideoDuration]);

  if (!open || !currentVideo) return null;

  return createPortal(
    <div className="tx-tuto">
      <div
        className="tx-tuto__overlay"
        role="presentation"
        onClick={persistClose}
      />

      <div
        className="tx-tuto__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tx-tuto-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tx-tuto__body">
          <div className="tx-tuto__main">
            <div className="tx-tuto__heading">
              <div className="tx-tuto__tags">
                <span className="tx-tuto__badge">{t('Nouveau')}</span>
                <span className="tx-tuto__kicker">
                  {t('Nouvelle fonctionnalité')}
                </span>
              </div>
              <h2 className="tx-tuto__title" id="tx-tuto-title">
                {t('Les tutoriels vidéo arrivent sur Typix Néo')}
              </h2>
              <p className="tx-tuto__subtitle">
                {t(
                  'Des vidéos courtes pour maîtriser chaque étape, à regarder quand vous voulez.'
                )}
              </p>
            </div>

            <div className="tx-tuto__player">
              <video
                ref={videoRef}
                key={currentVideo.src}
                src={currentVideo.src}
                controls={isPlaying}
                playsInline
                preload="metadata"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onLoadedMetadata={(e) => {
                  setVideoDuration(currentVideo.id, e.currentTarget.duration);
                }}
                onDurationChange={(e) => {
                  setVideoDuration(currentVideo.id, e.currentTarget.duration);
                }}
                onEnded={() => {
                  markWatched(currentVideo.id);
                  setIsPlaying(false);
                }}
              >
                <source src={currentVideo.src} type="video/mp4" />
              </video>

              {!isPlaying && (
                <button
                  type="button"
                  className="tx-tuto__play"
                  onClick={handlePlayClick}
                  aria-label={`${t('Regarder la vidéo')} : ${t(currentVideo.titleKey)}`}
                >
                  <PlayIcon />
                </button>
              )}
            </div>

            <div>
              <h3 className="tx-tuto__vtitle">{t(currentVideo.titleKey)}</h3>
              {currentVideo.descriptionKey && (
                <p className="tx-tuto__vdesc">
                  {t(currentVideo.descriptionKey)}
                </p>
              )}
            </div>
          </div>

          <aside className="tx-tuto__aside">
            <div className="tx-tuto__aside-head">
              <h3 className="tx-tuto__aside-title">
                {t('Bibliothèque des vidéos')}
              </h3>
              <button
                type="button"
                className="tx-tuto__close"
                aria-label={t('Fermer')}
                onClick={persistClose}
              >
                <CloseIcon />
              </button>
            </div>

            <div className="tx-tuto__count">
              {watchedCount}/{VIDEO_PLAYLIST.length} {t('vues')}
            </div>
            <div className="tx-tuto__progress">
              <div
                className="tx-tuto__progress-bar"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            <ul className="tx-tuto__list">
              {VIDEO_PLAYLIST.map((video, index) => {
                const isActive = index === activeIndex;
                const isWatched = watchedIds.includes(video.id);
                const durationLabel =
                  formatDuration(durations[video.id]) ||
                  video.duration ||
                  '–';

                return (
                  <li key={video.id}>
                    <button
                      type="button"
                      className={`tx-tuto__item${isWatched ? ' is-watched' : ''}`}
                      aria-current={isActive ? 'true' : 'false'}
                      onClick={() => selectVideo(index)}
                    >
                      <span className="tx-tuto__num">
                        {isWatched ? <CheckIcon /> : index + 1}
                      </span>
                      <span className="tx-tuto__info">
                        <span className="tx-tuto__item-title">
                          {t(video.titleKey)}
                        </span>
                        <span className="tx-tuto__item-dur">{durationLabel}</span>
                      </span>
                      <span className="tx-tuto__new">{t('Nouveau')}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>

        <div className="tx-tuto__footer">
          <label className="tx-tuto__check">
            <input
              type="checkbox"
              checked={dontShowAgain}
              disabled={isHiding}
              onChange={(e) => handleDontShowAgainChange(e.target.checked)}
            />
            {t('Ne plus afficher au démarrage')}
          </label>

          <div className="tx-tuto__actions">
            <button
              type="button"
              className="tx-tuto__btn tx-tuto__btn--secondary"
              onClick={persistClose}
            >
              {t('Plus tard')}
            </button>
            <button
              type="button"
              className="tx-tuto__btn tx-tuto__btn--primary"
              onClick={handlePrimaryAction}
            >
              {isLastVideo ? t('Terminer') : t('Vidéo suivante')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
